import { lumi } from '@/lib/lumi'
import { useEffect, useState } from 'react'

interface FormularioMapaCura {
  _id: string
  name: string
  email: string
  whatsapp: string
  idade: string
  createdAt: string
  updatedAt?: string
  q5?: string; q6?: string; q7?: string; q8?: string; q9?: string; q10?: string
  q11?: string; q12?: string; q13?: string; q14?: string; q15?: string; q16?: string
  q17?: string; q18?: string; q19?: string; q20?: string; q21?: string; q22?: string
  q23?: string; q24?: string; q25?: string; q26?: string; q27?: string; q28?: string
}

const FORM_LINK = 'https://jornada.reacendaluz.com.br/formulario/mapa'

const SECTIONS: { title: string; color: string; questions: { id: keyof FormularioMapaCura; num: number; label: string }[] }[] = [
  {
    title: 'Bloco Elemento Terra — TER',
    color: '#8FAE6F',
    questions: [
      { id: 'q5', num: 5, label: 'O que você quer TER na sua vida' },
      { id: 'q6', num: 6, label: 'O que te impede de decidir mudar sua vida agora' },
      { id: 'q7', num: 7, label: 'Sente que não merece prosperidade, abundância e estabilidade' },
      { id: 'q8', num: 8, label: 'Quem passou a ideia de que dinheiro é difícil, escasso ou perigoso' },
      { id: 'q9', num: 9, label: 'Vergonha de algo do passado que ainda carrega' },
      { id: 'q10', num: 10, label: 'O que evita decidir ou assumir por medo de errar ou decepcionar' },
    ],
  },
  {
    title: 'Bloco Elemento Água — SENTIR',
    color: '#5FA0D9',
    questions: [
      { id: 'q11', num: 11, label: 'Como você quer se SENTIR na sua vida' },
      { id: 'q12', num: 12, label: 'Quem não quer mais na vida, mas ainda mantém' },
      { id: 'q13', num: 13, label: 'Acredita que merece receber amor, cuidado e coisas boas' },
      { id: 'q14', num: 14, label: 'Alguém com quem se compara e sente que não é suficiente' },
      { id: 'q15', num: 15, label: 'Quem ensinou que demonstrar sentimentos é fraqueza' },
      { id: 'q16', num: 16, label: 'Relacionamento que drena energia e o que prende nele' },
    ],
  },
  {
    title: 'Bloco Elemento Fogo — FAZER',
    color: '#F09A5E',
    questions: [
      { id: 'q17', num: 17, label: 'O que você vai FAZER para criar a vida que deseja' },
      { id: 'q18', num: 18, label: 'O que fica adiando e por que não faz' },
      { id: 'q19', num: 19, label: 'Sente que não é boa o suficiente para ter sucesso' },
      { id: 'q20', num: 20, label: 'Alguém cuja opinião ou julgamento paralisa' },
      { id: 'q21', num: 21, label: 'Padrões de autossabotagem que se repetem' },
      { id: 'q22', num: 22, label: 'O que faz desanimar quando está quase chegando lá' },
    ],
  },
  {
    title: 'Bloco Elemento Ar — AMAR',
    color: '#D4C3EA',
    questions: [
      { id: 'q23', num: 23, label: 'Como vai AMAR e se relacionar na vida desejada' },
      { id: 'q24', num: 24, label: 'Alguém que ainda não perdoou e o que aconteceu' },
      { id: 'q25', num: 25, label: 'Consegue pedir ajuda ou prefere resolver sozinha' },
      { id: 'q26', num: 26, label: 'Relação com pai, mãe e figuras masculino/feminino' },
      { id: 'q27', num: 27, label: 'Raiva, ressentimento ou mágoa que ainda não soltou' },
      { id: 'q28', num: 28, label: 'O que, ou quem, impede de viver em paz e equilíbrio' },
    ],
  },
]

function formatDate(iso?: string) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function CopyLinkButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch { /* noop */ }
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase transition-all duration-150 active:scale-[0.97]"
      style={{
        color: copied ? '#22c55e' : '#E0B870',
        background: copied ? 'rgba(34,197,94,0.12)' : 'rgba(200,146,58,0.18)',
        border: `1px solid ${copied ? 'rgba(34,197,94,0.35)' : 'rgba(200,146,58,0.35)'}`,
      }}
    >
      {copied ? 'Copiado' : 'Copiar link'}
    </button>
  )
}

function FormCard({
  form,
  expanded,
  onToggle,
  onDelete,
}: {
  form: FormularioMapaCura
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-2xl transition-all duration-200 overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-start justify-between gap-3 p-4 sm:p-5">
        <button onClick={onToggle} className="min-w-0 flex-1 text-left">
          <p className="font-display italic text-[17px] sm:text-[18px] text-[var(--ink)] leading-snug truncate">
            {form.name}
          </p>
          <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 truncate">{form.email}</p>
          <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 truncate numerals">{form.whatsapp}</p>
          <p className="text-[11px] text-[var(--ink-soft)] font-body opacity-70 mt-1">
            Idade: <span className="numerals">{form.idade || '—'}</span>
            {' · '}
            {formatDate(form.createdAt)}
          </p>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onToggle}
            className="px-3 py-1.5 rounded-lg text-[11px] tracking-[0.15em] uppercase font-body transition-all"
            style={{
              background: 'rgba(200,146,58,0.14)',
              color: '#E0B870',
              border: '1px solid rgba(200,146,58,0.35)',
            }}
          >
            {expanded ? 'Recolher' : 'Ver respostas'}
          </button>
          <button
            onClick={onDelete}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 hover:bg-red-500/15 active:scale-90 group"
            title="Excluir formulário"
            aria-label="Excluir formulário"
          >
            <svg className="w-4 h-4 text-[var(--ink-soft)] group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="px-4 sm:px-5 pb-5 space-y-6 border-t border-white/8 pt-5">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: '#E0B870' }}>
              Dados Pessoais
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { label: 'Nome completo', value: form.name },
                { label: 'E-mail', value: form.email },
                { label: 'WhatsApp', value: form.whatsapp },
                { label: 'Idade', value: form.idade },
                { label: 'Data de preenchimento', value: formatDate(form.createdAt) },
              ].map(row => (
                <div key={row.label} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">{row.label}</p>
                  <p className="text-[13px] font-body text-[var(--ink)]">{row.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {SECTIONS.map(sec => (
            <div key={sec.title}>
              <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: sec.color }}>
                {sec.title}
              </p>
              <div className="space-y-3">
                {sec.questions.map(q => (
                  <div key={q.id as string} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">
                      <span className="numerals">{String(q.num).padStart(2, '0')}</span>
                      {' · '}
                      {q.label}
                    </p>
                    <p className="text-[13px] font-body text-[var(--ink)] whitespace-pre-wrap leading-relaxed">
                      {((form[q.id] as string) || '').trim() || <span className="italic opacity-50">(sem resposta)</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default function AdminFormulariosMapaCura() {
  const [forms, setForms] = useState<FormularioMapaCura[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FormularioMapaCura | null>(null)

  useEffect(() => {
    fetchForms()
  }, [])

  async function fetchForms() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.formulario_mapa_cura.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      setForms(list as FormularioMapaCura[])
    } catch (e) {
      console.error('Erro ao buscar Mapas de Cura:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    const id = deleteTarget._id
    setDeleteTarget(null)
    setForms(prev => prev.filter(f => f._id !== id))
    try {
      await lumi.entities.formulario_mapa_cura.delete(id)
    } catch (e) {
      console.error('Erro ao excluir Mapa de Cura:', e)
      fetchForms()
    }
  }

  return (
    <>
      <div
        className="rounded-2xl p-4 sm:p-5 mb-6"
        style={{ background: 'rgba(200,146,58,0.1)', border: '1px solid rgba(200,146,58,0.35)' }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-2" style={{ color: '#E0B870' }}>
          Link para envio à cliente
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <a
            href={FORM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-[14px] sm:text-[15px] font-body break-all hover:underline"
            style={{ color: '#E0B870' }}
          >
            {FORM_LINK}
          </a>
          <CopyLinkButton text={FORM_LINK} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#E0B870] border-t-transparent animate-spin" />
            <p className="text-[13px] font-body text-[var(--ink-soft)]">Carregando Mapas de Cura…</p>
          </div>
        </div>
      ) : forms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-[15px] font-display italic text-[var(--ink-soft)] mb-2">
            Nenhum Mapa de Cura recebido ainda
          </p>
          <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60">
            As respostas aparecerão aqui após o envio pelo link acima.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="rounded-xl p-3 sm:p-4 border border-white/8" style={{ background: 'rgba(255,255,255,0.025)' }}>
              <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">Total</p>
              <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: '#E0B870' }}>{forms.length}</p>
            </div>
          </div>
          <div className="space-y-3">
            {forms.map(f => (
              <FormCard
                key={f._id}
                form={f}
                expanded={expandedId === f._id}
                onToggle={() => setExpandedId(prev => prev === f._id ? null : f._id)}
                onDelete={() => setDeleteTarget(f)}
              />
            ))}
          </div>
          <p className="text-center text-[11px] font-body text-[var(--ink-soft)] opacity-50 mt-6 numerals">
            {forms.length} {forms.length === 1 ? 'mapa' : 'mapas'} exibido{forms.length === 1 ? '' : 's'}
          </p>
        </>
      )}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5"
             style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl border border-white/10"
               style={{ background: '#141c24' }}>
            <h3 className="font-display italic text-[20px] text-[var(--ink)] text-center mb-2">
              Excluir formulário
            </h3>
            <p className="text-[13px] text-[var(--ink-soft)] font-body text-center leading-relaxed mb-6">
              Deseja realmente excluir o Mapa de Cura de{' '}
              <span className="text-[var(--ink)] font-medium">{deleteTarget.name}</span>?
              Essa ação é permanente.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase active:scale-[0.97]"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--ink-soft)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase active:scale-[0.97]"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
